import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card, CardBody, Col, Container, Row, Spinner } from 'reactstrap';
import TransactionsApi, { TransactionStatusEnum } from '@/api/TransactionsApi';
import { Loading } from '@/components/ui/Loading';
import { AppPaths } from '@/configs/appPaths';
import { UUID } from '@/types/types';

export const TransactionStatus: React.FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const transactionId = searchParams.get('transactionId');

	const {
		data: transaction,
		isLoading,
		isError,
		refetch,
		isRefetching,
	} = useQuery({
		queryKey: [TransactionsApi.queryKey, transactionId as UUID],
		queryFn: TransactionsApi.getOne,
		enabled: !!transactionId,
		refetchInterval: (query) => {
			const data = query.state.data;
			return data?.status === TransactionStatusEnum.PENDING ? 30000 : false;
		},
		refetchOnWindowFocus: true,
	});

	useEffect(() => {
		if (transaction && transaction.status === TransactionStatusEnum.ACCEPTED) {
			setTimeout(() => {
				navigate(`/${AppPaths.PACKAGES}`);
			}, 5000);
		}
	}, [transaction, navigate]);

	if (!transactionId) {
		return (
			<div className="account-pages my-5 pt-sm-5">
				<Container>
					<Row className="justify-content-center">
						<Col md={8} lg={6} xl={5}>
							<Card className="overflow-hidden">
								<CardBody className="pt-0">
									<div className="text-center p-4">
										<Alert color="danger">
											<h5 className="alert-heading">Błąd</h5>
											<p className="mb-0">
												Nie podano ID transakcji. Sprawdź link i spróbuj
												ponownie.
											</p>
										</Alert>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</div>
		);
	}

	if (isLoading) {
		return <Loading />;
	}

	if (isError) {
		return (
			<div className="account-pages my-5 pt-sm-5">
				<Container>
					<Row className="justify-content-center">
						<Col md={8} lg={6} xl={5}>
							<Card className="overflow-hidden">
								<CardBody className="pt-0">
									<div className="text-center p-4">
										<Alert color="danger">
											<h5 className="alert-heading">Błąd</h5>
											<p className="mb-0">
												Nie udało się pobrać informacji o transakcji. Spróbuj
												ponownie później.
											</p>
											<button
												className="btn btn-primary mt-3"
												onClick={() => refetch()}
											>
												Spróbuj ponownie
											</button>
										</Alert>
									</div>
								</CardBody>
							</Card>
						</Col>
					</Row>
				</Container>
			</div>
		);
	}

	const getStatusContent = () => {
		if (!transaction) return null;
		
		switch (transaction.status) {
			case TransactionStatusEnum.PENDING:
				return (
					<div className="text-center">
						<div className="mb-4">
							<Spinner
								className="m-2"
								color="warning"
								style={{ width: '3rem', height: '3rem' }}
							/>
						</div>
						<Alert color="warning">
							<h5 className="alert-heading">Płatność w trakcie realizacji</h5>
							<p className="mb-0">
								Twoja płatność jest obecnie przetwarzana. Sprawdzamy status co
								30 sekund.
							</p>
							{isRefetching && (
								<div className="mt-2">
									<small className="text-muted">
										<Spinner size="sm" className="me-2" />
										Sprawdzanie statusu...
									</small>
								</div>
							)}
						</Alert>
						<div className="mt-3">
							<small className="text-muted">
								ID transakcji: {transaction.externalID}
							</small>
						</div>
					</div>
				);

			case TransactionStatusEnum.ACCEPTED:
				return (
					<div className="text-center">
						<div className="mb-4">
							<i
								className="bx bx-check-circle text-success"
								style={{ fontSize: '4rem' }}
							></i>
						</div>
						<Alert color="success">
							<h5 className="alert-heading">Płatność zakończona sukcesem!</h5>
							<p className="mb-0">
								Twoja płatność w wysokości{' '}
								<strong>
									{(transaction.amount / 100).toLocaleString('pl-PL', {
										style: 'currency',
										currency: 'PLN',
									})}
								</strong>{' '}
								została pomyślnie zrealizowana.
							</p>
							<hr />
							<p className="mb-0">
								Zostaniesz przekierowany do strony głównej za 5 sekund.
							</p>
						</Alert>
						<div className="mt-3">
							<small className="text-muted">
								ID transakcji: {transaction.externalID}
							</small>
						</div>
					</div>
				);

			case TransactionStatusEnum.REJECTED:
				return (
					<div className="text-center">
						<div className="mb-4">
							<i
								className="bx bx-x-circle text-danger"
								style={{ fontSize: '4rem' }}
							></i>
						</div>
						<Alert color="danger">
							<h5 className="alert-heading">Płatność odrzucona</h5>
							<p className="mb-0">
								Niestety, Twoja płatność w wysokości{' '}
								<strong>
									{(transaction.amount / 100).toLocaleString('pl-PL', {
										style: 'currency',
										currency: 'PLN',
									})}
								</strong>{' '}
								została odrzucona.
							</p>
							<hr />
							<p className="mb-0">
								Skontaktuj się z nami lub spróbuj ponownie z inną metodą
								płatności.
							</p>
						</Alert>
						<div className="mt-3">
							<small className="text-muted">
								ID transakcji: {transaction.externalID}
							</small>
						</div>
						<div className="mt-3">
							<button
								className="btn btn-primary"
								onClick={() => navigate(`/${AppPaths.PACKAGES}`)}
							>
								Powrót do logowania
							</button>
						</div>
					</div>
				);

			default:
				return (
					<div className="text-center">
						<Alert color="warning">
							<h5 className="alert-heading">Nieznany status</h5>
							<p className="mb-0">
								Status transakcji jest nieznany. Skontaktuj się z pomocą
								techniczną.
							</p>
						</Alert>
					</div>
				);
		}
	};

	return (
		<div className="account-pages my-5 pt-sm-5">
			<Container>
				<Row className="justify-content-center">
					<Col md={8} lg={6} xl={5}>
						<Card className="overflow-hidden">
							<CardBody className="pt-0">
								<div className="p-4">
									<div className="text-center mb-4">
										<h4 className="text-primary">Status płatności</h4>
										<p className="text-muted">
											Sprawdzanie statusu Twojej transakcji
										</p>
									</div>
									{getStatusContent()}
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		</div>
	);
};
